import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Plus, Minus } from 'lucide-react';
import { type FormField } from '@/types';

interface Ticket {
  id: string;
  name: string;
  description: string | null;
  price: number;
  capacity: number;
  formSchema: FormField[] | null;
  type: 'MEMBER_ONLY' | 'MEMBER_GUEST' | 'OPEN_FOR_ALL';
}

interface TicketCardProps {
  ticket: Ticket;
  isMember: boolean;
  cartCount: number;
  onUpdateCart: (ticket: Ticket, delta: number) => void;
  onLoginRequired: () => void;
}

export function TicketCard({
  ticket,
  isMember,
  cartCount,
  onUpdateCart,
  onLoginRequired,
}: TicketCardProps) {
  const isMemberOnly = ticket.type === 'MEMBER_ONLY' || ticket.type === 'MEMBER_GUEST';
  const canPurchase = !isMemberOnly || isMember;

  return (
    <div
      className={`flex min-w-0 flex-col gap-2 overflow-hidden rounded-lg border p-3 transition-colors ${
        canPurchase ? 'hover:border-primary/20' : 'opacity-75 bg-muted/30'
      }`}
    >
      <div className="flex min-w-0 flex-col gap-1.5">
        <p className="min-w-0 font-medium text-base leading-snug">{ticket.name}</p>
        {isMemberOnly && (
          <Badge variant="outline" className="h-5 w-fit max-w-full shrink-0 whitespace-nowrap text-[10px]">
            Members Only
          </Badge>
        )}
      </div>

      <div className="flex items-center justify-between gap-3">
        <p className="shrink-0 text-sm text-muted-foreground">
          {ticket.price === 0 ? 'Free' : `₹${ticket.price}`}
        </p>

        {canPurchase ? (
          <div className="flex shrink-0 items-center gap-1 rounded-xl border border-muted-foreground/10 bg-muted/50 p-1 shadow-inner backdrop-blur-sm">
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 rounded-lg transition-all hover:bg-background/80 disabled:opacity-30"
              onClick={() => onUpdateCart(ticket, -1)}
              disabled={cartCount === 0}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <span className="w-6 text-center text-sm font-bold tabular-nums">{cartCount}</span>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 rounded-lg text-primary transition-all hover:bg-background/80 hover:text-primary"
              onClick={() => onUpdateCart(ticket, 1)}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="h-8 max-w-[55%] shrink-0 whitespace-nowrap text-xs"
            onClick={onLoginRequired}
          >
            {isMember ? 'Members Only' : 'Login to Join'}
          </Button>
        )}
      </div>

      {ticket.description && (
        <p className="break-words text-xs text-muted-foreground">{ticket.description}</p>
      )}
      {!canPurchase && isMember && (
        <p className="text-[10px] font-medium italic text-destructive">
          * You need to join this community first.
        </p>
      )}
    </div>
  );
}
